import moment from 'moment'

import type { IPostData, IAccountData, CsvDownloadCommon } from './csv-download-common.service'

import { app } from '../../app.module'

interface IFbPostData extends IPostData {
  fbPostId: string
  likes: number
  comments: number
  shares: number
  engagements: number
  engagementRate: number
  videoViews: number
}

interface IFbAccountdata extends IAccountData {
  likes: number
  comments: number
  shares: number
  engagements: number
  engagementRate: number
  internationalFlg: number
}

/**
 * CsvダウンロードFacebookサービス
 */
export class CsvDownloadFacebook {
  constructor(
    private CsvDownloadCommon: CsvDownloadCommon,
    private $filter: any
  ) {}

  /**
   * 投稿CSVデータをセット
   * @param {IFbPostData[]} postDatas FB投稿データ
   * @returns {string[][]} CSVデータ
   */
  public setPostCsv(postDatas: IFbPostData[]): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    const header = [
      'No',
      '投稿日付',
      'アカウント名',
      '大カテゴリ',
      '小カテゴリ',
      'フォロワー数',
      '投稿タイプ',
      '投稿内容',
      'いいね！数',
      'コメント数',
      'シェア数',
      '反応数',
      '反応率(%)',
      '動画再生数',
      '投稿タグ',
      '画像URL',
      '投稿先URL',
      'アカウントID',
      '投稿ID',
    ]

    // ヘッダーを格納
    csvData.push(header)
    // データを格納
    postDatas.forEach((postData, index) => {
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, postData.userTagNames)

      csvData.push([
        index + 1,
        moment(postData.publishDate).format('YYYY/MM/DD HH:mm'),
        postData.accountScreenName,
        postData.accountCategoryName,
        postData.accountSubCategoryName,
        postData.postDateFollowers,
        this.CsvDownloadCommon.convertPostType(postData.type),
        this.CsvDownloadCommon.convertPostMessage(postData.message),
        postData.likes,
        postData.comments,
        postData.shares,
        postData.engagements,
        this.CsvDownloadCommon.convertPercentage(postData.engagementRate),
        postData.videoViews !== 0 ? postData.videoViews : null,
        tagLists,
        postData.thumbnailUrl,
        'https://www.facebook.com/' + postData.accountId + '/posts/' + postData.fbPostId,
        postData.accountId,
        postData.fbPostId,
      ])
    })
    return csvData
  }

  /**
   * アカウントCSVデータをセット
   * @param {IFbAccountdata[]} accountDatas アカウントデータ
   * @param {boolean} dataPerPost 1投稿あたりフラグ
   * @returns {string[][]} CSVデータ
   */
  public setAccountCsv(accountDatas: IFbAccountdata[], dataPerPost: boolean): string[][] {
    // CSVデータ
    const csvData = []
    // ヘッダー
    let header = []

    if (dataPerPost === true) {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        'いいね！数(1投稿あたり)',
        'コメント数(1投稿あたり)',
        'シェア数(1投稿あたり)',
        '反応数(1投稿あたり)',
        '反応率(%)',
        '投稿数',
        'アカウントタグ',
        'グローバル',
      ]
    } else {
      header = [
        'No',
        'アカウント名',
        'ブランド名',
        '大カテゴリ',
        '小カテゴリ',
        'フォロワー数',
        'フォロワー増減率(%)',
        'いいね！数',
        'コメント数',
        'シェア数',
        '反応数',
        '反応率(%)',
        '投稿数',
        'アカウントタグ',
        'グローバル',
      ]
    }

    // ヘッダーを格納
    csvData.push(header)

    // データを格納
    accountDatas.forEach((account, index) => {
      const engageData = [account.likes, account.comments, account.shares, account.engagements]
      const ratioData = [
        this.CsvDownloadCommon.convertPercentage(account.engagementRate),
        account.posts,
      ]

      // 1投稿ごとの反応数が選択されている場合は、反応数を投稿数ごとに割る
      if (dataPerPost === true) {
        engageData.forEach((element, index) => {
          engageData[index] = this.$filter('setNumberFormat')(
            element,
            1,
            'number',
            true,
            account.posts
          )
        })
      }
      // タグリスト
      let tagLists = ''

      // グローバルタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.globalTagNames)
      // グループタグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.groupTagNames)
      // ユーザータグを追加
      tagLists = this.CsvDownloadCommon.concatTag(tagLists, account.tags.userTagNames)

      // フォロワー増減率
      let followersUpdownRate = '-'
      if (account.followersUpdownRate !== null) {
        followersUpdownRate = String(account.followersUpdownRate)
      }

      let accountData = [
        index + 1,
        account.screenName,
        account.brandName,
        account.categoryName,
        account.subCategoryName,
        account.followers,
        followersUpdownRate,
      ]

      accountData = accountData.concat(
        engageData,
        ratioData,
        [tagLists],
        [account.internationalFlg]
      )

      csvData.push(accountData)
    })

    return csvData
  }

  /**
   * アカウント詳細のグラフCSVをセット
   * @param  {string} graphType グラフタイプ
   * @param  {boolean} dataPerObjectFlg 1投稿ごとフラグ(1アカウントごとフラグ)
   * @param {array} periodArray 期間リスト
   * @param  {object} data データ
   * @returns {string[][]} CSVデータ
   */
  public setAccountChangeCsv(
    graphType: string,
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    switch (graphType) {
      case 'respond':
        return this.setAccountChangeRespondCsv(dataPerObjectFlg, periodArray, data)
      case 'respond_rate':
        return this.CsvDownloadCommon.setAccountChangeRespondRateCsv(periodArray, data)
      case 'post_count':
        return this.CsvDownloadCommon.setAccountChangePostCount(dataPerObjectFlg, periodArray, data)
    }
  }

  /**
   * アカウントの投稿の対象CSVデータをセット
   * @param  {string} analyticsTab 対象名
   * @param  {string} snsType SNSタイプ
   * @param  {boolean} dataPerPostFlg 1投稿ごとフラグ
   * @param  {array} datas データ
   * @return {string[][]} CSVデータ
   */
  public setAccountTargetCsv(
    targetName: string,
    dataPerPostFlg: boolean,
    datas: any[]
  ): string[][] {
    const csvData = []

    // 1投稿ごとではない場合
    if (dataPerPostFlg === false) {
      csvData.push([
        targetName,
        '投稿数',
        'いいね！数',
        'コメント数',
        'シェア数',
        '反応数',
        '反応率',
      ])

      datas.forEach((data) => {
        csvData.push([
          data.targetIndex,
          data.posts,
          data.likes,
          data.comments,
          data.shares,
          data.engagements,
          this.CsvDownloadCommon.convertPercentage(data.engagementRate),
        ])
      })
    } else {
      // 1投稿ごと場合
      csvData.push([
        targetName,
        '投稿数',
        'いいね！数(1投稿あたり)',
        'コメント数(1投稿あたり)',
        'シェア数(1投稿あたり)',
        '反応数(1投稿あたり)',
        '反応率',
      ])

      datas.forEach((data) => {
        csvData.push([
          data.targetIndex,
          data.posts,
          data.likesPerPost,
          data.commentsPerPost,
          data.sharesPerPost,
          data.engagementsPerPost,
          this.CsvDownloadCommon.convertPercentage(data.engagementRate),
        ])
      })
    }

    return csvData
  }

  /**
   * アカウント詳細グラフの反応数CSVをセット
   * @param dataPerObjectFlg 一投稿あたりフラグ
   * @param periodArray 期間リスト
   * @param data データ
   * @returns {string[][]} CSVデータ
   */
  private setAccountChangeRespondCsv(
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): string[][] {
    // CSVデータ
    const csvData = []

    if (dataPerObjectFlg === false) {
      csvData.push(['期間', 'いいね！数', 'コメント数', 'シェア数', '反応数'])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([
          periodArray[key],
          data.respondLikes[key],
          data.respondComments[key],
          data.respondShares[key],
          data.respondLikes[key] + data.respondComments[key] + data.respondShares[key],
        ])
      }
    } else {
      csvData.push([
        '期間',
        'いいね！数(1投稿あたり)',
        'コメント数(1投稿あたり)',
        'シェア数(1投稿あたり)',
        '反応数(1投稿あたり)',
      ])

      for (let key = 0; key < periodArray.length; key = key + 1) {
        csvData.push([
          periodArray[key],
          data.respondLikesPerPost[key],
          data.respondCommentsPerPost[key],
          data.respondSharesPerPost[key],
          data.respondLikesPerPost[key] +
            data.respondCommentsPerPost[key] +
            data.respondSharesPerPost[key],
        ])
      }
    }

    return csvData
  }
}

// CSVダウンロード用のサービスを定義
app.service('CsvDownloadFacebook', CsvDownloadFacebook)
